import { pack, rgba, bayer } from "../shared/color";
import { CELL, DEBRIS, SOLID } from "../shared/defs";
import type { Sprite } from "./art";
import type { Letters, Particles } from "./entities";
import { SCALE } from "./rig";
import { drawRot, RotSprite, type Pix } from "./rotsprite";
import { makeHold, type Hold } from "./stick";
import { slabAlong, type Slab, type Slabs } from "./slabs";
import type { World } from "./world";

/**
 * Digger's drill charge. Numbers live here so the shot can be tuned in one place.
 * Launch 1000 px/s, flight gravity 640, inherit 0.22 vx / 0.12 vy.
 * Bore radius 11, up to 0.6s or 800px, gap grace 36px, then coast up to 1.05s.
 * A coasting bit that meets more page before its aim point bores again.
 * Fuse 0.6s from launch, so press-to-boom stays short wherever it is aimed.
 * Text bore 500–600 px/s. Images 550. Other boxes 750.
 * Blocks over 25,000 px² are bored too but never take tunnel damage.
 * The first small block the bit enters takes 75, and so does each small parent above it.
 * Big blocks: 70 per hit, 150 if the blast is carved into it within 20px,
 * 90 for the forward vent out of a page-wide band. One shot spends each cap once.
 * Detach budget: one shot may knock loose at most half of min(30% of the page, 120k cells).
 * Breaks past 6000 cells in one frame wait, in order, for the next frame, so a big boom never hitches.
 * The boom itself counts as 3000, so a panel bigger than the rest of that frame breaks one frame later.
 * Blast carve 44, rough 0.14, direct block damage 220, nearby 90 inside 84.
 * Bodies 60 every 0.16s, cap 120 per body per shot, blast 105. Boss uses the same cap,
 * so a square hit on the boss lands near 165, about twice a rocket.
 * Cooldown 1.3s, one owned bit, hit-stop 0.055s, bore rumble 0.16, blast rumble 0.8.
 * Bonus coins 30%, cap 3 per shot, budgeted. Flight gives up after 3.6s.
 * Player blast 140. Ember life 0.6s, cap 120, rim only. Lip scorch 0.9.
 * Spin 14 rad/s in flight, 26 while boring.
 * Letter fling 12px along the tunnel, 80 on the blast.
 * Knockback power 1600, reach 80 * 2.5, lift 0.42.
 * Owned shots detonate at the aim point. The bloom is the rocket's, at 1.3×, flash 0.9.
 */
export const DRILL = {
  cd: 1.3,
  speed: 1000,
  flightG: 640,
  inheritX: 0.22,
  inheritY: 0.12,
  boreR: 11,
  boreTime: 0.6,
  boreDist: 800,
  gap: 36,
  fuse: 0.6,
  blastR: 44,
  blastRough: 0.14,
  blockDmg: 220,
  nearDmg: 90,
  nearReach: 84,
  entryDmg: 75,
  tick: 60,
  tickEvery: 0.16,
  tickCap: 120,
  blastBody: 105,
  bodyR: 14,
  hitStop: 0.055,
  boreTrauma: 0.16,
  boomTrauma: 0.8,
  coinChance: 0.3,
  coinCap: 3,
  player: 140,
  ember: 0.6,
  emberCap: 120,
  char: 0.9,
  spinFly: 14,
  spinBore: 26,
  coastTime: 1.05,
  flightTime: 3.6,
  letterBore: 12,
  letterBlast: 80,
  knock: 1600,
  knockReach: 80 * 2.5,
  knockLift: 0.42,
  floorStop: 18,
  big: 25000,
  bigCap: 70,
  coreCap: 150,
  coreR: 20,
  ventCap: 90,
  detachShare: 0.5,
  frameCells: 6000,
  boomCells: 3000,
  band: 0.72,
  breach: 300,
  breachR: 14,
  breachSide: 96,
  textFast: 600,
  textSlow: 500,
  imageSpeed: 550,
  boxSpeed: 750,
} as const;

export interface DrillHost {
  world: World;
  letters: Letters;
  chunks: Slabs;
  parts: Particles;
  fling(index: number, vx: number, vy: number, av: number, burn: number): void;
  bite(x: number, y: number, r: number, dmg: number, cap: number, token: number, now: number, every: number): void;
  blast(x: number, y: number, r: number, dmg: number): void;
  cache(index: number): boolean;
  spoil(x: number, y: number): boolean;
  sfx(id: string): void;
  /** Hold trauma at least this high. */
  rumble(n: number): void;
  punch(seconds: number): void;
  knock(x: number, y: number, reach: number, power: number, lift: number): void;
  players(x: number, y: number, radius: number, dmg: number): void;
  flash(amount: number, tint: string): void;
  /** Page chips, copied before the crater is carved. */
  chips(x: number, y: number): void;
  /** Rocket-style bloom. No second damage pass. */
  spectacle(x: number, y: number): void;
  onBoom(): void;
}

const BIT_W = 22;
const BIT_H = 9;
const GRIP_X = 3;
const GRIP_Y = 4;
const TIP_X = 20;
const TIP_Y = 4;
const FORE_X = 11;
const FORE_Y = 6;

const OUT = pack(22, 16, 28);
const DARK = pack(48, 54, 64);
const STEEL = pack(150, 160, 174);
const HI = pack(226, 232, 240);
const BRASS = pack(196, 148, 64);
const BRASS_D = pack(122, 84, 32);
const TIP = pack(92, 98, 110);

interface Bit {
  x: number;
  y: number;
  vx: number;
  vy: number;
  dirX: number;
  dirY: number;
  phase: "fly" | "bore" | "coast";
  age: number;
  boreT: number;
  boreDist: number;
  gap: number;
  coast: number;
  spin: number;
  ghost: boolean;
  token: number;
  spoils: number;
  entryX: number;
  entryY: number;
  sx: number;
  sy: number;
  /** Straight-line distance from the muzzle to the aim point. Infinity if unknown. */
  td: number;
  dead: boolean;
  /** World removed-cell count at launch, for the detach budget. */
  rc0: number;
  /** Small blocks that already took entry damage. */
  entered: Set<number>;
}

interface Pending {
  bit: Bit;
  id: number;
  dmg: number;
  dirX: number;
  dirY: number;
  cap: number;
  carved: boolean;
}

interface Ember {
  x: number;
  y: number;
  life: number;
  max: number;
  hot: boolean;
}

interface Flash {
  x: number;
  y: number;
  a: number;
  life: number;
}

interface Puff {
  ox: number;
  oy: number;
  life: number;
  max: number;
  seed: number;
}

interface Boom {
  x: number;
  y: number;
  t: number;
  max: number;
  puffs: Puff[];
}

function inBit(x: number, y: number): boolean {
  if (y < 2 || y > 6) return false;
  if (x >= 2 && x <= 17) return true;
  if ((x === 18 || x === 19) && y >= 3 && y <= 5) return true;
  return x === 20 && y === 4;
}

/** Four frames, same silhouette. Flutes step two pixels each frame. */
function bitFrames(): Pix[] {
  const frames: Pix[] = [];
  for (let f = 0; f < 4; f++) {
    const data = new Uint32Array(BIT_W * BIT_H);
    const paint = (x: number, y: number, color: number) => {
      if (x < 0 || y < 0 || x >= BIT_W || y >= BIT_H) return;
      data[y * BIT_W + x] = color;
    };
    for (let y = 0; y < BIT_H; y++) {
      for (let x = 0; x < BIT_W; x++) {
        if (!inBit(x, y)) continue;
        if (x <= 4) {
          paint(x, y, y <= 3 ? BRASS : BRASS_D);
          if (x === 4 && y === 3) paint(x, y, HI);
          continue;
        }
        if (x >= 17) {
          paint(x, y, y === 3 ? HI : y === 4 ? STEEL : TIP);
          continue;
        }
        const band = Math.floor((x + f * 2) / 2) % 3;
        let color = STEEL;
        if (y === 3) color = band === 0 ? HI : STEEL;
        else if (band === 0 && y >= 5) color = DARK;
        else if (band === 1 && y <= 2) color = HI;
        else if (band === 2) color = y >= 4 ? DARK : STEEL;
        paint(x, y, color);
      }
    }
    const body = data.slice();
    for (let y = 0; y < BIT_H; y++) {
      for (let x = 0; x < BIT_W; x++) {
        if (body[y * BIT_W + x]) continue;
        let edge = false;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= BIT_W || ny >= BIT_H) continue;
          if (body[ny * BIT_W + nx]) edge = true;
        }
        if (edge) paint(x, y, OUT);
      }
    }
    frames.push({ w: BIT_W, h: BIT_H, data });
  }
  return frames;
}

let cached: { sprite: Sprite; flight: RotSprite[] } | null = null;

function art(): { sprite: Sprite; flight: RotSprite[] } {
  if (cached) return cached;
  const framePix = bitFrames();
  const img = new Image();
  // Cache key for spriteRot only. A data URL so the browser does not request a file.
  img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
  const sprite: Sprite = {
    img,
    w: framePix[0].w,
    h: framePix[0].h,
    frames: 4,
    pix: framePix[0],
    trimX: 0,
    trimY: 0,
    framePix,
  };
  cached = {
    sprite,
    flight: framePix.map((pix) => new RotSprite(pix, TIP_X, TIP_Y, 32)),
  };
  return cached;
}

/** In-hand sprite. Frame 0 only; the flying bit cycles all four. */
export function drillHold(): Hold {
  const sprite = art().sprite;
  return makeHold(sprite, [GRIP_X, GRIP_Y], [TIP_X, TIP_Y], [FORE_X, FORE_Y], true, false, 32);
}

/** Hotbar icon. Same frame-0 pixels as the bit in the hand. */
export function paintDrillIcon(icon: HTMLCanvasElement) {
  const g = icon.getContext("2d");
  if (!g) return;
  g.imageSmoothingEnabled = false;
  const pix = art().sprite.pix;
  const ox = Math.floor((icon.width - pix.w) / 2);
  const oy = Math.floor((icon.height - pix.h) / 2);
  for (let y = 0; y < pix.h; y++) {
    for (let x = 0; x < pix.w; x++) {
      const color = pix.data[y * pix.w + x];
      if ((color >>> 24) < 128) continue;
      g.fillStyle = rgba(color);
      g.fillRect(ox + x, oy + y, 1, 1);
    }
  }
}

function clamp(n: number, a: number, b: number): number {
  return n < a ? a : n > b ? b : n;
}

export class DrillRig {
  private bits: Bit[] = [];
  private embers: Ember[] = [];
  private flashes: Flash[] = [];
  private booms: Boom[] = [];
  private charred = new Set<number>();
  /** Breaks held for a later frame, oldest first. */
  private pending: Pending[] = [];
  /** Cells broken loose since the last draw. */
  private frameCells = 0;
  private flushing = false;
  private seq = 1;
  private cd = 0;

  constructor(private host: DrillHost) {}

  ready(): boolean {
    return this.cd <= 0 && !this.bits.some((bit) => !bit.ghost);
  }

  /** 0 while a bit is out or the cooldown just started, 1 when the next shot is ready. */
  meter(): number {
    if (this.bits.some((bit) => !bit.ghost)) return 0;
    if (this.cd <= 0) return 1;
    return 1 - this.cd / DRILL.cd;
  }

  grinding(): boolean {
    return this.bits.some((bit) => !bit.ghost && bit.phase === "bore");
  }

  snapshot() {
    const bit = this.bits.find((item) => !item.ghost);
    return {
      cd: this.cd,
      live: !!bit,
      phase: bit?.phase ?? "",
      x: bit?.x ?? 0,
      y: bit?.y ?? 0,
    };
  }

  launch(x: number, y: number, angle: number, pvx: number, pvy: number, ghost: boolean, tx = NaN, ty = NaN) {
    if (!ghost && !this.ready()) return;
    const vx = Math.cos(angle) * DRILL.speed + pvx * DRILL.inheritX;
    const vy = Math.sin(angle) * DRILL.speed + pvy * DRILL.inheritY;
    const sp = Math.hypot(vx, vy) || 1;
    this.bits.push({
      x, y, vx, vy,
      dirX: vx / sp,
      dirY: vy / sp,
      phase: "fly",
      age: 0,
      boreT: 0,
      boreDist: 0,
      gap: 0,
      coast: 0,
      spin: 0,
      ghost,
      token: this.seq++,
      spoils: 0,
      entryX: x,
      entryY: y,
      sx: x,
      sy: y,
      td: Number.isFinite(tx) && Number.isFinite(ty) ? Math.hypot(tx - x, ty - y) : Infinity,
      dead: false,
      rc0: this.host.world.removedCells,
      entered: new Set<number>(),
    });
    if (!ghost) this.cd = DRILL.cd;
    this.flashes.push({ x, y, a: angle, life: 0.08 });
    this.muzzleFx(x, y, angle);
  }

  step(dt: number) {
    this.flush();
    for (const bit of this.bits) this.moveBit(bit, dt);
    for (const bit of this.bits) {
      if (bit.dead) continue;
      if (bit.phase === "bore") this.boreFx(bit);
      if (!bit.ghost && bit.phase === "bore") this.host.rumble(DRILL.boreTrauma);
      if (!bit.ghost) this.host.bite(bit.x, bit.y, DRILL.bodyR, DRILL.tick, DRILL.tickCap, bit.token, bit.age, DRILL.tickEvery);
    }
    this.bits = this.bits.filter((bit) => !bit.dead);
    this.ease(dt);
  }

  /** Hit-stop keeps the blast painting while the world holds still. */
  ease(dt: number) {
    this.flush();
    this.cd = Math.max(0, this.cd - dt);
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      this.flashes[i].life -= dt;
      if (this.flashes[i].life <= 0) this.flashes.splice(i, 1);
    }
    for (let i = this.embers.length - 1; i >= 0; i--) {
      const ember = this.embers[i];
      ember.life -= dt;
      ember.y -= 10 * dt;
      if (ember.life <= 0) this.embers.splice(i, 1);
    }
    for (let i = this.booms.length - 1; i >= 0; i--) {
      const boom = this.booms[i];
      boom.t += dt;
      for (const puff of boom.puffs) {
        puff.life -= dt;
        puff.oy -= 18 * dt;
      }
      if (boom.t >= boom.max) this.booms.splice(i, 1);
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    this.frameCells = 0;
    const { flight } = art();
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    for (const bit of this.bits) this.drawShaft(ctx, bit);
    this.drawEmbers(ctx);
    for (const bit of this.bits) {
      const ang = bit.phase === "bore" ? Math.atan2(bit.dirY, bit.dirX) : Math.atan2(bit.vy, bit.vx);
      const frame = ((bit.spin / (Math.PI * 2)) * 4) | 0;
      const idx = ((frame % 4) + 4) % 4;
      drawRot(ctx, flight[idx].get(ang, false), bit.x, bit.y, SCALE);
      if (bit.phase === "bore") this.hotTip(ctx, bit.x, bit.y, ang);
    }
    for (const flash of this.flashes) this.drawFlash(ctx, flash);
    for (const boom of this.booms) this.drawBoom(ctx, boom);
    ctx.restore();
  }

  private moveBit(bit: Bit, dt: number) {
    const speed = Math.hypot(bit.vx, bit.vy);
    const steps = Math.max(1, Math.ceil((speed * dt) / 4));
    const h = dt / steps;
    for (let i = 0; i < steps; i++) {
      if (bit.dead) return;
      this.substep(bit, h);
    }
  }

  private substep(bit: Bit, dt: number) {
    const world = this.host.world;
    bit.age += dt;
    bit.spin += (bit.phase === "bore" ? DRILL.spinBore : DRILL.spinFly) * dt;
    if (bit.age >= DRILL.fuse) {
      this.detonate(bit);
      return;
    }
    if (bit.phase === "fly") {
      if (bit.age >= DRILL.flightTime) {
        this.detonate(bit);
        return;
      }
      bit.vy += DRILL.flightG * dt;
    } else if (bit.phase === "coast") {
      bit.vy += DRILL.flightG * dt;
      bit.coast += dt;
      if (bit.coast >= DRILL.coastTime) {
        this.detonate(bit);
        return;
      }
    } else {
      const sampled = this.sample(bit);
      const speed = this.boreSpeed(sampled.text, sampled.image, sampled.box, sampled.n);
      bit.vx = bit.dirX * speed;
      bit.vy = bit.dirY * speed;
      if (sampled.open) bit.gap += speed * dt;
      else bit.gap = 0;
      bit.boreT += dt;
      bit.boreDist += speed * dt;
      if (bit.gap > DRILL.gap) {
        bit.phase = "coast";
        bit.coast = 0;
        return;
      }
      if (bit.boreT >= DRILL.boreTime || bit.boreDist >= DRILL.boreDist) {
        this.detonate(bit);
        return;
      }
    }
    const x1 = bit.x + bit.vx * dt;
    const y1 = bit.y + bit.vy * dt;
    const hit = bit.phase === "bore" ? null : this.segmentHit(bit.x, bit.y, x1, y1);
    if (hit && bit.phase === "fly") {
      bit.x = hit.x;
      bit.y = hit.y;
      this.enterBore(bit);
      this.dig(bit, dt);
      return;
    }
    if (hit && bit.phase === "coast") {
      bit.x = hit.x;
      bit.y = hit.y;
      // More page before the aim point: keep drilling instead of popping on the far side of a gap.
      if (Math.hypot(bit.x - bit.sx, bit.y - bit.sy) < bit.td - 24) {
        this.enterBore(bit);
        this.dig(bit, dt);
        return;
      }
      this.detonate(bit);
      return;
    }
    if ((x1 < 6 && bit.vx < 0) || (x1 > world.W - 6 && bit.vx > 0)) {
      bit.x = clamp(x1, 8, world.W - 8);
      bit.y = y1;
      this.detonate(bit);
      return;
    }
    bit.x = x1;
    bit.y = y1;
    if (bit.td > 24 && Math.hypot(bit.x - bit.sx, bit.y - bit.sy) >= bit.td) {
      this.detonate(bit);
      return;
    }
    if (bit.phase === "bore") {
      if (bit.y > world.H + DRILL.floorStop) {
        this.detonate(bit);
        return;
      }
      this.dig(bit, dt);
    }
  }

  private enterBore(bit: Bit) {
    const sp = Math.hypot(bit.vx, bit.vy) || 1;
    bit.dirX = bit.vx / sp;
    bit.dirY = bit.vy / sp;
    bit.phase = "bore";
    bit.boreT = 0;
    bit.boreDist = 0;
    bit.gap = 0;
    bit.entryX = bit.x;
    bit.entryY = bit.y;
  }

  /** Text stays in 250–330. Photos sit near 130, so a 0.6s bore is a short hole in a picture and a longer one in copy. */
  private boreSpeed(text: number, image: number, box: number, n: number): number {
    if (image > 0 && image >= text && image >= box) return DRILL.imageSpeed;
    if (box > text && box > 0) return DRILL.boxSpeed;
    const density = n > 0 ? text / n : 0;
    const span = DRILL.textFast - DRILL.textSlow;
    return clamp(DRILL.textFast - density * span, DRILL.textSlow, DRILL.textFast);
  }

  /** Forward half-disk, ahead of the carved pocket, so a ghost and the owner agree. */
  private sample(bit: Bit): { open: boolean; text: number; image: number; box: number; n: number } {
    const r = DRILL.boreR;
    let n = 0;
    let solid = 0;
    let text = 0;
    let image = 0;
    let box = 0;
    for (let dy = -r; dy <= r; dy += 4) {
      for (let dx = -r; dx <= r; dx += 4) {
        if (dx * dx + dy * dy > r * r) continue;
        const px = bit.x + bit.dirX * 14 + dx;
        const py = bit.y + bit.dirY * 14 + dy;
        const along = (px - bit.x) * bit.dirX + (py - bit.y) * bit.dirY;
        if (along < 13) continue;
        n++;
        if (this.weightAt(px, py) > 0) solid++;
        const kind = this.matterAt(px, py);
        if (kind === "text") text++;
        else if (kind === "image") image++;
        else if (kind === "box") box++;
      }
    }
    if (this.slabNear(bit.x + bit.dirX * 16, bit.y + bit.dirY * 16, r)) {
      n++;
      solid++;
      box++;
    }
    return { open: solid === 0, text, image, box, n };
  }

  private weightAt(x: number, y: number): number {
    const world = this.host.world;
    if (y >= world.H) return 1.4;
    const col = Math.floor(x / CELL);
    const row = Math.floor(y / CELL);
    if (row >= world.GH) return 1.4;
    if (col < 0 || row < 0 || col >= world.GW || row >= world.GH) return 0;
    const i = row * world.GW + col;
    const mat = world.mat[i];
    if (mat === SOLID) {
      const owner = world.owner[i];
      const block = owner >= 0 ? world.blocks[owner] : undefined;
      const area = block ? Math.max(1, block.w * block.h) : 400;
      return 0.95 + Math.min(1.55, Math.sqrt(area) / 165);
    }
    if (mat === DEBRIS) return 0.7;
    if (world.lcount[i] > 0) return 0.35;
    return 0;
  }

  private structureAt(x: number, y: number): boolean {
    const world = this.host.world;
    if (y >= world.H - 1) return true;
    const col = Math.floor(x / CELL);
    const row = Math.floor(y / CELL);
    if (row >= world.GH) return true;
    if (col < 0 || row < 0 || col >= world.GW) return false;
    const i = row * world.GW + col;
    if (world.lcount[i] > 0) return true;
    if (world.mat[i] >= SOLID) return true;
    return this.pointSlab(x, y);
  }

  private matterAt(x: number, y: number): "image" | "text" | "box" | "none" {
    const world = this.host.world;
    if (y >= world.H) return "box";
    const col = Math.floor(x / CELL);
    const row = Math.floor(y / CELL);
    if (row >= world.GH || col < 0 || row < 0 || col >= world.GW) return "none";
    const i = row * world.GW + col;
    const letters = world.lcount[i] > 0;
    if (world.mat[i] === SOLID) {
      const owner = world.owner[i];
      const block = owner >= 0 ? world.blocks[owner] : undefined;
      if (block && block.w * block.h > DRILL.big) return letters ? "text" : "box";
      if (block?.kind === "image") return "image";
      if (letters) return "text";
      return "box";
    }
    if (world.mat[i] === DEBRIS) return "box";
    if (letters) return "text";
    return "none";
  }

  private segmentHit(x0: number, y0: number, x1: number, y1: number): { x: number; y: number } | null {
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const n = Math.max(1, Math.ceil(dist / 2));
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const x = x0 + (x1 - x0) * t;
      const y = y0 + (y1 - y0) * t;
      if (this.structureAt(x, y)) return { x, y };
    }
    return null;
  }

  private pointSlab(x: number, y: number): boolean {
    for (const slab of this.host.chunks.list) {
      if (slabAlong(slab, x, y, x + 0.01, y) >= 0) return true;
    }
    return false;
  }

  private slabNear(x: number, y: number, r: number): boolean {
    for (const slab of this.host.chunks.list) {
      const reach = r + Math.max(slab.bw, slab.bh) * 0.45;
      if (Math.hypot(slab.x - x, slab.y - y) <= reach) return true;
    }
    return false;
  }

  private dig(bit: Bit, dt: number) {
    if (bit.ghost || bit.dead) return;
    const world = this.host.world;
    world.beginCarve();
    const cells = new Map<number, number>();
    world.carve(bit.x, bit.y, DRILL.boreR, 0.1, (_x, _y, _color, _mat, owner) => {
      if (owner >= 0) cells.set(owner, (cells.get(owner) ?? 0) + 1);
    });
    this.scorch(1);
    for (const [id, n] of cells) {
      const block = world.blocks[id];
      if (block && block.w * block.h > DRILL.big) continue;
      if (bit.entered.size === 0) this.entryHit(bit, id);
      const area = Math.max(1, (block?.w ?? 1) * (block?.h ?? 1));
      const dmg = dt * (48 / (1 + Math.sqrt(area) / 140)) + n * 0.05;
      this.hurtBlock(bit, id, dmg, -bit.dirX, -bit.dirY);
    }
    this.flingLetters(bit.x, bit.y, DRILL.letterBore, -bit.dirX, -bit.dirY, 420);
    this.chewSlabs(bit.x, bit.y, DRILL.boreR + 8, 240);
  }

  /** First small block the bit enters, and each small parent above it, take one solid hit. */
  private entryHit(bit: Bit, id: number) {
    const world = this.host.world;
    let up = id;
    while (up >= 0 && !bit.entered.has(up)) {
      const block = world.blocks[up];
      if (!block || block.w * block.h > DRILL.big) break;
      bit.entered.add(up);
      this.hurtBlock(bit, up, DRILL.entryDmg, -bit.dirX, -bit.dirY);
      up = block.parent;
    }
  }

  /** Page-wide bands eat a round blast. Vent forward so the shot still clears the next piece. */
  private vent(bit: Bit, x: number, y: number, hurt: Set<number>) {
    const world = this.host.world;
    if (!this.onBand(x, y)) return;
    const sp = Math.hypot(bit.dirX, bit.dirY) || 1;
    const nx = bit.dirX / sp;
    const ny = bit.dirY / sp;
    const reach = DRILL.breach;
    world.beginCarve();
    const steps = Math.ceil(reach / 10);
    const lip = Math.ceil(DRILL.breachR / CELL) + 1;
    for (let i = 1; i <= steps; i++) {
      const px = x + nx * (reach * i / steps);
      const py = y + ny * (reach * i / steps);
      world.carve(px, py, DRILL.breachR, 0);
      const col = Math.floor(px / CELL);
      const row = Math.floor(py / CELL);
      for (const sign of [-1, 1]) {
        const cx = col + Math.round(-ny * lip * sign);
        const cy = row + Math.round(nx * lip * sign);
        if (cx < 0 || cy < 0 || cx >= world.GW || cy >= world.GH) continue;
        world.charCell(cy * world.GW + cx, 0.55);
      }
    }
    const bandW = world.W * DRILL.band;
    world.blocks.forEach((block, index) => {
      if (!block.alive || hurt.has(index) || block.w * block.h <= DRILL.big) return;
      if (block.w >= bandW) return;
      if (!this.nearSegment(x, y, nx, ny, reach, DRILL.breachSide, block)) return;
      this.hurtBlock(bit, index, DRILL.blockDmg, nx, ny, DRILL.ventCap);
      hurt.add(index);
    });
    const parts = this.host.parts;
    for (let i = 1; i <= 4; i++) {
      const px = x + nx * (reach * i / 5);
      const py = y + ny * (reach * i / 5);
      const life = 0.14 + Math.random() * 0.1;
      parts.spawn({
        x: px, y: py,
        vx: nx * 70 + (Math.random() - 0.5) * 36,
        vy: ny * 70 + (Math.random() - 0.5) * 36,
        life, max: life, size: 1.4,
        color: i < 2 ? "#fff4e0" : "#ffb15a",
        grav: 420, rubble: false, glow: true, kind: "spark",
      });
    }
  }

  private onBand(x: number, y: number): boolean {
    const world = this.host.world;
    const need = world.W * DRILL.band;
    const seen = new Set<number>();
    for (let i = 0; i < world.blocks.length; i++) {
      const block = world.blocks[i];
      if (x < block.x || y < block.y || x > block.x + block.w || y > block.y + block.h) continue;
      let id = i;
      while (id >= 0 && !seen.has(id)) {
        seen.add(id);
        const item = world.blocks[id];
        if (item.w >= need) return true;
        id = item.parent;
      }
    }
    return false;
  }

  private nearSegment(x: number, y: number, nx: number, ny: number, reach: number, side: number, block: { x: number; y: number; w: number; h: number }): boolean {
    const samples = Math.ceil(reach / 16);
    const side2 = side * side;
    for (let i = 1; i <= samples; i++) {
      const px = x + nx * (reach * i / samples);
      const py = y + ny * (reach * i / samples);
      const dx = Math.max(block.x - px, 0, px - (block.x + block.w));
      const dy = Math.max(block.y - py, 0, py - (block.y + block.h));
      if (dx * dx + dy * dy <= side2) return true;
    }
    return false;
  }

  /**
   * Big blocks take at most `cap` per hit. A blast carved into one within coreR of the boom
   * gets the higher core cap, so a hit squarely inside a big panel can break it,
   * while a page-wide band grazed from outside only cracks.
   */
  private hurtBlock(bit: Bit, id: number, dmg: number, dirX: number, dirY: number, cap: number = DRILL.bigCap, carved = false) {
    const world = this.host.world;
    const block = world.blocks[id];
    if (!block?.alive || dmg <= 0) return;
    if (block.w * block.h > DRILL.big) {
      if (carved) {
        const dx = Math.max(block.x - bit.x, 0, bit.x - (block.x + block.w));
        const dy = Math.max(block.y - bit.y, 0, bit.y - (block.y + block.h));
        if (Math.hypot(dx, dy) <= DRILL.coreR) cap = DRILL.coreCap;
      }
      dmg = Math.min(dmg, cap);
    }
    if (block.canDetach && !this.mayDetach(bit, id, block, dmg)) {
      if (block.lost >= block.cells * 0.38) return;
      dmg = Math.min(dmg, block.hp - block.damage - 0.5);
    }
    if (dmg <= 0) return;
    if (block.canDetach && (block.lost >= block.cells * 0.38 || block.damage + dmg >= block.hp)) {
      // A break cuts the block out of the page. Spread big ones over frames, keeping their order.
      const size = this.subtree(id);
      if (!this.flushing && (this.pending.length > 0 || (this.frameCells > 0 && this.frameCells + size > DRILL.frameCells))) {
        this.pending.push({ bit, id, dmg, dirX, dirY, cap, carved });
        return;
      }
      this.frameCells += size;
    }
    const cache = this.host.cache(id);
    world.damageBlock(id, dmg, dirX, dirY, bit.x, bit.y);
    if (!cache && !block.alive) this.spoil(bit, block.x + block.w / 2, block.y + block.h / 2);
  }

  /**
   * One shot may only knock loose a share of the page: half of 30% of its cells, at most 60k.
   * A hit that would break a block past that budget cracks it instead.
   */
  private mayDetach(bit: Bit, id: number, block: { lost: number; cells: number; damage: number; hp: number }, dmg: number): boolean {
    const shed = block.lost >= block.cells * 0.38;
    if (!shed && block.damage + dmg < block.hp) return true;
    const world = this.host.world;
    const budget = DRILL.detachShare * Math.min(0.3 * world.totalCells, 120000);
    return world.removedCells - bit.rc0 + this.subtree(id) <= budget;
  }

  /** Run held breaks, oldest first, while this frame still has room. */
  private flush() {
    while (this.pending.length > 0) {
      const item = this.pending[0];
      const block = this.host.world.blocks[item.id];
      if (block?.alive && this.frameCells > 0 && this.frameCells + this.subtree(item.id) > DRILL.frameCells) return;
      this.pending.shift();
      this.flushing = true;
      this.hurtBlock(item.bit, item.id, item.dmg, item.dirX, item.dirY, item.cap, item.carved);
      this.flushing = false;
    }
  }

  /** Cells still standing in a block and everything it would carry away. */
  private subtree(id: number): number {
    const blocks = this.host.world.blocks;
    const stack = [id];
    const seen = new Set<number>();
    let n = 0;
    while (stack.length) {
      const i = stack.pop()!;
      if (seen.has(i)) continue;
      seen.add(i);
      const b = blocks[i];
      if (!b?.alive) continue;
      n += Math.max(0, b.cells - b.lost);
      for (const c of b.children) stack.push(c);
    }
    return n;
  }

  private spoil(bit: Bit, x: number, y: number) {
    if (bit.ghost || bit.spoils >= DRILL.coinCap) return;
    if (Math.random() > DRILL.coinChance) return;
    if (this.host.spoil(x, y)) bit.spoils++;
  }

  private flingLetters(x: number, y: number, radius: number, dirX: number, dirY: number, power: number) {
    const letters = this.host.letters;
    const radial = dirX === 0 && dirY === 0;
    letters.query(x - radius, y - radius, x + radius, y + radius, (i) => {
      const gx = letters.x[i] + letters.w[i] / 2;
      const gy = letters.y[i] + letters.h[i] / 2;
      const dx = gx - x;
      const dy = gy - y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > radius) return;
      const nx = radial ? dx / dist : dirX;
      const ny = radial ? dy / dist : dirY;
      const fall = 1 - dist / radius;
      const kick = power * (0.35 + 0.65 * fall);
      this.host.fling(i, nx * kick + (Math.random() - 0.5) * 80, ny * kick - 90 - Math.random() * 110, (Math.random() - 0.5) * 36, -1);
    });
  }

  private chewSlabs(x: number, y: number, reach: number, speed: number) {
    for (const slab of this.host.chunks.list.slice()) {
      const limit = reach + Math.max(slab.bw, slab.bh) * 0.35;
      if (Math.hypot(slab.x - x, slab.y - y) <= limit) this.host.chunks.shatter(slab, x, y, speed);
    }
  }

  private scorch(reach: number) {
    const world = this.host.world;
    const rim = world.rim(reach);
    for (let i = 0; i < rim.cells.length; i++) {
      if (rim.ring[i] > reach) continue;
      const cell = rim.cells[i];
      if (this.charred.has(cell)) continue;
      this.charred.add(cell);
      const lip = rim.ring[i] <= 1;
      world.charCell(cell, lip ? DRILL.char : 0.48);
      if (!lip || this.embers.length >= DRILL.emberCap) continue;
      const col = cell % world.GW;
      const row = Math.floor(cell / world.GW);
      if (bayer(col, row) > 0.34) continue;
      const life = DRILL.ember * (0.72 + Math.random() * 0.28);
      this.embers.push({
        x: col * CELL + 1,
        y: row * CELL + 1,
        life,
        max: life,
        hot: Math.random() < 0.4,
      });
    }
  }

  private detonate(bit: Bit) {
    if (bit.dead) return;
    bit.dead = true;
    const { x, y } = bit;
    this.booms.push({
      x, y, t: 0, max: 0.72,
      puffs: Array.from({ length: 9 }, () => {
        const ang = Math.random() * Math.PI * 2;
        const dist = 8 + Math.random() * 36;
        const life = 0.45 + Math.random() * 0.28;
        return { ox: Math.cos(ang) * dist, oy: Math.sin(ang) * dist, life, max: life, seed: Math.random() * 8 };
      }),
    });
    this.blastFx(x, y, bit.dirX, bit.dirY);
    if (bit.ghost) return;
    this.host.chips(x, y);
    this.frameCells += DRILL.boomCells;
    const world = this.host.world;
    world.beginCarve();
    const owners = new Set<number>();
    world.carve(x, y, DRILL.blastR, DRILL.blastRough, (_cx, _cy, _color, _mat, owner) => {
      if (owner >= 0) owners.add(owner);
    });
    this.scorch(2);
    const hurt = new Set<number>();
    for (const id of owners) {
      this.hurtBlock(bit, id, DRILL.blockDmg, 0, -1, DRILL.bigCap, true);
      hurt.add(id);
    }
    world.blocks.forEach((block, index) => {
      if (!block.alive || hurt.has(index)) return;
      const dx = Math.max(block.x - x, 0, x - (block.x + block.w));
      const dy = Math.max(block.y - y, 0, y - (block.y + block.h));
      const dist = Math.hypot(dx, dy);
      if (dist >= DRILL.nearReach) return;
      this.hurtBlock(bit, index, DRILL.nearDmg * (1 - dist / DRILL.nearReach), 0, -1);
      hurt.add(index);
    });
    this.vent(bit, x, y, hurt);
    this.flingLetters(x, y, DRILL.letterBlast, 0, 0, 980);
    this.chewSlabs(x, y, DRILL.blastR * 1.15, 460);
    this.host.blast(x, y, DRILL.blastR * 1.05, DRILL.blastBody);
    this.host.knock(x, y, DRILL.knockReach, DRILL.knock, DRILL.knockLift);
    this.host.players(x, y, DRILL.blastR, DRILL.player);
    this.host.rumble(DRILL.boomTrauma);
    this.host.punch(DRILL.hitStop);
    this.host.spectacle(x, y);
    this.host.sfx("drillBlast");
    this.host.onBoom();
  }

  private muzzleFx(x: number, y: number, angle: number) {
    const parts = this.host.parts;
    for (let n = 0; n < 6; n++) {
      const a = angle + Math.PI + (Math.random() - 0.5) * 0.8;
      const s = 90 + Math.random() * 180;
      const life = 0.12 + Math.random() * 0.16;
      parts.spawn({
        x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
        life, max: life, size: 1.2, color: "#fff4e0", grav: 980, rubble: false, glow: true, kind: "spark",
      });
    }
    parts.smoke(x, y, -Math.cos(angle) * 40, -Math.sin(angle) * 40, 4, 0.28, "#6e5844");
  }

  private boreFx(bit: Bit) {
    const parts = this.host.parts;
    const backX = -bit.dirX;
    const backY = -bit.dirY;
    for (let n = 0; n < 2; n++) {
      const life = 0.14 + Math.random() * 0.18;
      const s = 70 + Math.random() * 150;
      parts.spawn({
        x: bit.entryX + (Math.random() - 0.5) * 4,
        y: bit.entryY + (Math.random() - 0.5) * 4,
        vx: backX * s + (Math.random() - 0.5) * 50,
        vy: backY * s + (Math.random() - 0.5) * 50,
        life, max: life, size: 1.2, color: "#fff6e4", grav: 980, rubble: false, glow: true, kind: "spark",
      });
    }
    if (Math.random() < 0.7) {
      parts.smoke(
        bit.entryX, bit.entryY,
        backX * (20 + Math.random() * 30), backY * (16 + Math.random() * 24) - 8,
        3 + Math.random() * 2.4, 0.45 + Math.random() * 0.25, "#7a5a42",
      );
    }
    const color = this.host.world.colorAt(bit.x - bit.dirX * 6, bit.y - bit.dirY * 6);
    parts.chip(
      bit.x, bit.y,
      backX * (140 + Math.random() * 180) + (Math.random() - 0.5) * 60,
      backY * (140 + Math.random() * 180) - 40,
      color, 0.55, 4.5,
    );
  }

  private blastFx(x: number, y: number, dirX: number, dirY: number) {
    const parts = this.host.parts;
    const world = this.host.world;
    for (let n = 0; n < 16; n++) {
      const ang = (n / 16) * Math.PI * 2;
      const s = 160 + Math.random() * 280;
      const life = 0.16 + Math.random() * 0.28;
      parts.spawn({
        x, y, vx: Math.cos(ang) * s, vy: Math.sin(ang) * s - 40,
        life, max: life, size: 1.3, color: "#ffe6b0", grav: 980, rubble: false, glow: true, kind: "spark",
      });
    }
    for (let n = 0; n < 8; n++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = Math.random() * 28;
      parts.smoke(
        x + Math.cos(ang) * dist, y + Math.sin(ang) * dist,
        Math.cos(ang) * 36, Math.sin(ang) * 24 - 28,
        5 + Math.random() * 4, 0.7 + Math.random() * 0.4, n % 2 ? "#6a4a38" : "#4a382c",
      );
    }
    for (let n = 0; n < 12; n++) {
      const ang = Math.atan2(dirY, dirX) + Math.PI + (Math.random() - 0.5) * 2.2;
      const s = 120 + Math.random() * 260;
      const px = x + Math.cos(ang) * 8;
      const py = y + Math.sin(ang) * 8;
      parts.chip(px, py, Math.cos(ang) * s, Math.sin(ang) * s - 60, n % 3 ? world.colorAt(px, py) : pack(110, 78, 52), 0.7, 5);
    }
  }

  private hotTip(ctx: CanvasRenderingContext2D, x: number, y: number, ang: number) {
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    ctx.fillStyle = "#fff6ea";
    ctx.fillRect(x - 1, y - 1, 2, 2);
    ctx.fillStyle = "#ffb15a";
    ctx.fillRect(x + dx * 3 - 1, y + dy * 3 - 1, 2, 2);
    ctx.fillStyle = "#e07a32";
    ctx.fillRect(x + dx * 5 - 1, y + dy * 5, 2, 2);
  }

  private drawFlash(ctx: CanvasRenderingContext2D, flash: Flash) {
    const k = Math.max(0, flash.life / 0.08);
    ctx.globalAlpha = k;
    const dx = Math.cos(flash.a);
    const dy = Math.sin(flash.a);
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = i < 2 ? "#fffaf2" : "#ffd27a";
      ctx.fillRect(flash.x + dx * i * 3 - 1, flash.y + dy * i * 3 - 1, 2, 2);
      if (i > 0) ctx.fillRect(flash.x + dx * i * 3 - dy * 3 - 1, flash.y + dy * i * 3 + dx * 3 - 1, 2, 2);
    }
    ctx.globalAlpha = 1;
  }

  private drawEmbers(ctx: CanvasRenderingContext2D) {
    for (const ember of this.embers) {
      ctx.globalAlpha = Math.max(0, ember.life / ember.max);
      ctx.fillStyle = ember.hot ? "#fff1d0" : "#ff9a3a";
      ctx.fillRect(ember.x, ember.y, 2, 2);
    }
    ctx.globalAlpha = 1;
  }

  /** Dithered dark core of the shaft. The rim stays clear so embers read as the lip, not a sand column. */
  private drawShaft(ctx: CanvasRenderingContext2D, bit: Bit) {
    if (bit.phase === "fly") return;
    const dist = Math.hypot(bit.x - bit.entryX, bit.y - bit.entryY);
    if (dist < 4) return;
    const r = Math.max(4, DRILL.boreR - 4);
    const steps = Math.max(1, Math.ceil(dist / 4));
    ctx.fillStyle = "#100810";
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const cx = bit.entryX + (bit.x - bit.entryX) * t;
      const cy = bit.entryY + (bit.y - bit.entryY) * t;
      for (let oy = -r; oy <= r; oy += 2) {
        for (let ox = -r; ox <= r; ox += 2) {
          const edge = (ox * ox + oy * oy) / (r * r);
          if (edge > 0.62) continue;
          const px = Math.round(cx + ox);
          const py = Math.round(cy + oy);
          if (bayer(px, py) > 0.4 + edge) continue;
          ctx.globalAlpha = 0.78;
          ctx.fillRect(px, py, 2, 2);
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  private drawBoom(ctx: CanvasRenderingContext2D, boom: Boom) {
    const t = boom.t;
    const { x, y } = boom;
    if (t < 0.05) {
      const core: Array<[number, number, string]> = [
        [0, 0, "#fffaf2"],
        [2, 0, "#fffaf2"],
        [-2, 0, "#ffe7a0"],
        [0, 2, "#ffe7a0"],
        [0, -2, "#ffd27a"],
        [2, -2, "#ffb15a"],
        [-2, 2, "#ff8a2a"],
      ];
      for (const [ox, oy, color] of core) {
        ctx.fillStyle = color;
        ctx.fillRect(x + ox - 1, y + oy - 1, 2, 2);
      }
    }
    for (const puff of boom.puffs) {
      if (puff.life <= 0) continue;
      const px = Math.round(x + puff.ox);
      const py = Math.round(y + puff.oy);
      ctx.globalAlpha = Math.max(0, puff.life / puff.max) * 0.9;
      for (let oy = 0; oy < 4; oy++) {
        for (let ox = 0; ox < 4; ox++) {
          if (bayer(px + ox + puff.seed, py + oy) > 0.55) continue;
          ctx.fillStyle = (ox + oy) % 3 === 0 ? "#8a6248" : "#3e2c24";
          ctx.fillRect(px + ox * 2, py + oy * 2, 2, 2);
        }
      }
    }
    ctx.globalAlpha = 1;
  }
}
